import { SPKApi } from "api"
import {
  ActionButton,
  Alert,
  BackButton,
  DataStatus,
  TextArea,
  TextEditor
} from "components"
import { ContentState, convertFromHTML, EditorState } from "draft-js"
import { Formik, useFormik } from "formik"
import { memo, useEffect, useState } from "react"
import { Card, Col, Modal, Row } from "react-bootstrap"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { DateConvert, GuestMiddleware } from "utilities"
import * as Yup from "yup"
import { FormSPK, InfoSection, TableBarangJadi } from "./Section"

const DetailSPK = ({ setNavbarTitle }) => {
  const history = useHistory()
  const location = useLocation()
  const { id_spk } = useParams()
  const { no_spk, tab } = location.state || {}

  const ContentDetailSPK = memo(() => {
    const [dataSPK, setDataSPK] = useState({})
    const [approveStatus, setApproveStatus] = useState("V")
    const [dataApproval, setDataApproval] = useState([])
    const [isPageLoading, setIsPageLoading] = useState(false)
    const [isFetchingFailed, setIsFetchingFailed] = useState(false)
    const [dataBarangJadi, setDataBarangJadi] = useState([])
    const [alertConfig, setAlertConfig] = useState({
      show: false,
      variant: "primary",
      text: ""
    })
    const [modalConfig, setModalConfig] = useState({
      show: false,
      type: "",
      title: ""
    })
    const [textEditorStateCatatan, setTextEditorStateCatatan] = useState(() => {
      return EditorState.createWithContent(
        ContentState.createFromBlockArray(convertFromHTML(`<p>Catatan :<br />`))
      )
    })

    const getInitialData = () => {
      setIsPageLoading(true)

      SPKApi.getSingle({ no_spk })
        .then((spk) => {
          const behavior = String(spk?.data?.data?.behavior).toUpperCase()
          const mapDataBarangJadi =
            spk?.data?.data?.detail?.detail_barang_jadi?.map((val) =>
              Object({
                ...val,
                satuan: val.satuan,
                qty: val.qty ?? 0,
                harga_satuan: val.unit_cost ?? 0,
                rounded: val.rounded ?? 0
              })
            )

          setDataSPK(spk?.data?.data.detail ?? {})
          setDataBarangJadi(mapDataBarangJadi ?? [])
          setApproveStatus(behavior ?? "V")
          setDataApproval(spk?.data?.data?.approval ?? [])
        })
        .catch((err) => {
          setIsFetchingFailed(true)
          setAlertConfig({
            show: true,
            variant: "danger",
            text: "Data gagal dimuat!"
          })
        })
        .finally(() => setIsPageLoading(false))
    }

    const formInitialValues = {
      id_spk: id_spk,
      id_penawaran: dataSPK?.id_penawaran ?? "",
      tgl_spk: dataSPK?.tgl_spk ?? "",
      no_spk: dataSPK?.no_spk ?? ""
    }
    const formik = useFormik({
      enableReinitialize: true,
      initialValues: formInitialValues
    })

    useEffect(() => {
      setNavbarTitle("Surat Perjanjian Kerja")
      getInitialData()
    }, [])

    useEffect(() => {
      setTextEditorStateCatatan(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(dataSPK?.catatan ?? "<p></p>")
          )
        )
      )
      return () => {}
    }, [dataSPK])

    const ModalSection = ({ values, isSubmitting, handleSubmit }) => {
      const ModalContent = () => (
        <>
          <Modal.Body className="text-center">
            <p>
              <h6>
                <b>
                  {modalConfig.title === "APP"
                    ? approveStatus === "A"
                      ? "Approve"
                      : "Verify"
                    : modalConfig.title}{" "}
                  dengan catatan :
                </b>
              </h6>
              <span>{values.catatan}</span>
            </p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <ActionButton
              variant="outline-secondary"
              text="Batal"
              className="px-4"
              onClick={() =>
                setModalConfig({
                  ...modalConfig,
                  show: false
                })
              }
            />
            <ActionButton
              text={
                modalConfig.type === "APP"
                  ? approveStatus === "A"
                    ? "Approve"
                    : "Verify"
                  : modalConfig.type === "REJ"
                  ? "Reject"
                  : "Revise"
              }
              variant={
                modalConfig.type === "APP"
                  ? "success"
                  : modalConfig.type === "REV"
                  ? "warning"
                  : "danger"
              }
              className="m-1 px-3 text-white"
              onClick={handleSubmit}
              loading={isSubmitting}
            />
          </Modal.Footer>
        </>
      )

      return (
        <Modal
          show={modalConfig.show}
          onHide={() =>
            setModalConfig({
              ...modalConfig,
              show: false
            })
          }>
          <Modal.Header closeButton className="py-2 d-flex align-items-center">
            <Modal.Title>
              <small>{modalConfig.title}</small>
            </Modal.Title>
          </Modal.Header>
          <ModalContent />
        </Modal>
      )
    }

    const FormCard = ({
      values,
      handleChange,
      dirty,
      validateForm,
      errors
    }) => {
      const InfoItem = ({ title1, value1, title2, value2 }) => (
        <div className="col-md-4 mb-4">
          <small>{title1}</small>
          <br />
          <b>{value1}</b>
          <div className="mt-1" />
          <small>{title2}</small>
          <br />
          <b>{value2}</b>
        </div>
      )

      return (
        <Card className="mt-4 mb-5">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <b>Approval Surat Perjanjian Kerja</b>
          </Card.Header>
          <Card.Body>
            <div className="d-flex align-content-between flex-wrap">
              {dataApproval.map((val, index) => (
                <InfoItem
                  key={index}
                  title1={
                    val.status_approval == "VER"
                      ? `PEMERIKSA ${val.approval_level}`
                      : "PENGESAH"
                  }
                  value1={val.nama_karyawan ?? "-"}
                  title2={"Catatan"}
                  value2={val.catatan ?? "-"}
                />
              ))}
            </div>
            <hr />
            <div>
              <TextArea
                label="Catatan"
                name="catatan"
                placeholder="Masukan catatan"
                value={values.catatan}
                onChange={handleChange}
                error={errors.catatan && true}
                errorText={errors.catatan}
              />
              <div className="d-flex justify-content-end mt-3">
                <ActionButton
                  text="Reject"
                  variant="danger"
                  className="m-1"
                  onClick={() => {
                    if (dirty) {
                      setModalConfig({
                        show: true,
                        type: "REJ",
                        title: (
                          <span className="text-danger">
                            Reject Surat Perjanjian Kerja
                          </span>
                        )
                      })
                    }
                    validateForm()
                  }}
                />
                {approveStatus !== "A" && (
                  <ActionButton
                    text="Revise"
                    variant="warning"
                    className="m-1 text-white"
                    onClick={() => {
                      if (dirty) {
                        setModalConfig({
                          show: true,
                          type: "REV",
                          title: (
                            <span className="text-warning">
                              Revise Surat Perjanjian Kerja
                            </span>
                          )
                        })
                      }
                      validateForm()
                    }}
                  />
                )}
                <ActionButton
                  text={approveStatus === "A" ? "Approve" : "Verify"}
                  variant="success"
                  className="m-1"
                  onClick={() => {
                    if (dirty) {
                      setModalConfig({
                        show: true,
                        type: "APP",
                        title: (
                          <span className="text-success">
                            Approve Surat Perjanjian Kerja
                          </span>
                        )
                      })
                    }
                    validateForm()
                  }}
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      )
    }

    const CatatanApproval = () => {
      const InfoItem = ({ title1, value1, title2, value2 }) => (
        <div className="mb-2">
          <small>{title1}</small>
          <br />
          <b>{value1}</b>
          <div className="mt-1" />
          <small>{title2}</small>
          <br />
          <b>{value2}</b>
        </div>
      )

      return (
        <Card className="mt-4 mb-5">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <b>Catatan Approval Surat Perjanjian Kerja</b>
          </Card.Header>
          <Card.Body>
            <Row>
              {dataSPK?.stakeholder?.map(
                (val, index) =>
                  index !== 0 &&
                  val.status_approval !== "PEN" && (
                    <Col sm>
                      <InfoItem
                        title1={
                          val.status_approval === "VER" ||
                          val.status_approval === "REV"
                            ? `Pemeriksa ${
                                val.approval_level !== "0"
                                  ? val.approval_level
                                  : ""
                              }`
                            : val.status_approval === "APP"
                            ? "Pengesah"
                            : "Di Tolak Oleh"
                        }
                        value1={val.nama_karyawan ?? "-"}
                        title2="Catatan"
                        value2={val.catatan ?? "-"}
                      />
                    </Col>
                  )
              )}
            </Row>
          </Card.Body>
        </Card>
      )
    }

    return (
      <>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <b>Detail Data SPK</b>
            <BackButton onClick={() => history.goBack()} />
          </Card.Header>
          <Card.Body>
            <Alert
              showCloseButton
              show={alertConfig.show}
              variant={alertConfig.variant}
              text={alertConfig.text}
              onClose={() => setAlertConfig({ ...alertConfig, show: false })}
            />
            {isPageLoading ? (
              <DataStatus loading={true} text="Memuat data . . ." />
            ) : isFetchingFailed ? (
              <DataStatus text="Data gagal dimuat, tidak dapat menambah data!" />
            ) : (
              <>
                <InfoSection data={dataSPK} />
                <hr />
                <FormSPK formik={formik} type="DETAIL" />
                <TableBarangJadi
                  dataBarangJadi={dataBarangJadi}
                  setDataBarangJadi={setDataBarangJadi}
                />
                <TextEditor
                  readOnly
                  label="Catatan"
                  placeholder="Tuliskan teks catatan"
                  editorState={textEditorStateCatatan}
                />
              </>
            )}
          </Card.Body>
        </Card>
        {!isPageLoading && tab !== "history" && (
          <Formik
            initialValues={{ catatan: "" }}
            validationSchema={Yup.object().shape({
              catatan: Yup.string().required("Masukan Catatan")
            })}
            onSubmit={(values) => {
              const finalValues = {
                ...values,
                no_transaksi: dataSPK.no_spk,
                status_approval: modalConfig.type,
                tgl_approval: DateConvert(new Date()).default,
                approval_baseline: dataSPK.baseline
              }

              SPKApi.approve(finalValues)
                .then((res) => {
                  history.push(
                    "/human-resource/approval/surat-perjanjian-kerja",
                    {
                      alert: {
                        show: true,
                        variant: "primary",
                        text: `${
                          modalConfig.type == "APP"
                            ? "Approve"
                            : modalConfig.type == "REV"
                            ? "Revise"
                            : "Reject"
                        } data berhasil!`
                      }
                    }
                  )
                })
                .catch((err) => {
                  setAlertConfig({
                    variant: "danger",
                    text: `Ubah data gagal! (${err.response.data.message})`
                  })
                })
                .finally(() => setModalConfig({ show: false }))
            }}>
            {({
              values,
              isSubmitting,
              handleChange,
              handleSubmit,
              validateForm,
              dirty,
              errors,
              touched
            }) => (
              <>
                <GuestMiddleware>
                  <FormCard
                    values={values}
                    handleChange={handleChange}
                    dirty={dirty}
                    validateForm={validateForm}
                    errors={errors}
                    touched={touched}
                  />
                </GuestMiddleware>
                <ModalSection
                  values={values}
                  isSubmitting={isSubmitting}
                  handleChange={handleChange}
                  handleSubmit={handleSubmit}
                />
              </>
            )}
          </Formik>
        )}

        {!isPageLoading && tab === "history" && <CatatanApproval />}
      </>
    )
  })

  return <ContentDetailSPK />
}

export default DetailSPK
